/**
 * The two bulk actions of `docs/13-tasks-section-proposal.md` §2 — *"Bulk select (mark done,
 * change due)"*.
 *
 * A selection spans boards, and each board has its **own** task mapping: its own status column,
 * its own due column and its own idea of what *done* means. So both actions are "group the
 * selection by board, resolve that board's mapping, write one `cells.bulkSet` per board" — one
 * `batch_change_pulses_column_value` per board, exactly as the grid's own bulk bar produces
 * (P-110), and never one write per row.
 *
 * A board the action cannot be performed on is **skipped and named**, never silently dropped:
 * a board with no status column in its mapping, or one whose status column nominates no done
 * label, has no definition of done (`specs/M10-tasks.md` §2: *"A status column that nominates no
 * done label leaves every task on that board open — the conservative reading, not 'a label
 * called Done'"*).
 *
 * **And a board that *refuses* the write is skipped the same way** (P-293, the 2026-09-17
 * incident): 580 rows across 21 boards, the 7th board answered `Not allowed to cell.update`, and
 * because the loop had no per-board `try`, the write stopped there — six boards written, the
 * toast printing the raw server error, the list never reloaded, and the person told nothing
 * about which boards had been touched. Every board therefore stands on its own now: its failure
 * is one `skipped` entry carrying the server's own sentence, the loop goes on to the next board,
 * the undo captured for a board that failed is dropped (nothing was written, so there is nothing
 * to put back), and the caller reloads the list whatever came back.
 */
import { getColumnUi, normaliseColumnSettings } from '../board-view/columns/registry';
import { columnForRole, toBoardColumn, toRenderContext } from './inline-editors';
import type { TaskRowView, TaskStatusLabelOptionView, TasksSource } from './types';

/** One cell put back the way it was — what an **Undo** of a bulk action is made of (P-285). */
export interface BulkCellWrite {
  boardId: string;
  itemId: string;
  columnId: string;
  /** The item's own canonical value, exactly as `tasks.itemContext` read it back. */
  value: unknown;
}

export interface BulkOutcome {
  /** How many rows were written. */
  written: number;
  /** Boards that could not take the write, with the reason to print. */
  skipped: { boardName: string; reason: string }[];
  /**
   * The **inverse Tasks call** that puts this action back, for the actions that write no cell —
   * archive and unarchive (P-301 §3).
   *
   * They cannot be undone through {@link BulkCellWrite}, because nothing about a cell changed:
   * the row's own `archived_at` did. The undo is therefore the opposite service call over
   * exactly the items that really moved (`changed`, never the whole selection), which is the
   * same "put back only what went in" rule {@link keepWritten} applies to a cell undo.
   */
  undoArchive?: { action: 'archive' | 'unarchive'; itemIds: string[] };
  /**
   * The writes that would put every row back — empty when the action offers no undo.
   *
   * It is captured **before** the write and per item, because the previous value is per item:
   * three tasks marked done were three different statuses, and an undo that wrote one label back
   * over all three would be a second bulk edit wearing an undo's label.
   */
  undo: BulkCellWrite[];
}

/**
 * How many rows an action is willing to read back before writing, so it can offer an Undo.
 *
 * Capturing costs one `boards.read` per row (`TasksSource.itemContext` is the only permission-
 * checked way to see an item's *real* cell value), so it is bounded. Above the bound the action
 * still runs — "Select all 800 in this tab · Mark done" must work — and the toast simply offers
 * no Undo rather than 800 reads nobody asked for. The number is this round's own (**ASSUMED**):
 * a selection a person ticked by hand is far below it, and one above it came from Select all.
 */
export const BULK_UNDO_LIMIT = 50;

/**
 * `cells.bulkSet` takes at most 500 item ids per call (`services/cells.ts#bulkSetCellValueInput`).
 * A "Select all 800 in this tab" therefore has to be chunked, and chunking here rather than at
 * the call site keeps P-110's shape: one batch per board per chunk, never one write per row.
 */
export const BULK_WRITE_CHUNK = 500;

function byBoard(rows: readonly TaskRowView[]): Map<string, TaskRowView[]> {
  const map = new Map<string, TaskRowView[]>();
  for (const row of rows) {
    const list = map.get(row.boardId);
    if (list) list.push(row);
    else map.set(row.boardId, [row]);
  }
  return map;
}

/** Rows the api itself left out of a batch — see `BulkCellWriteResultView.refused` (P-293). */
export interface BulkRefusal {
  itemId: string;
  itemName: string;
}

/** What one board's write did: the rows that went in, and the rows the api named back. */
interface BoardWrite {
  written: number;
  refused: BulkRefusal[];
}

/** One board, one column, one value — chunked at the router's own cap. */
async function writeCells(
  source: TasksSource,
  boardId: string,
  itemIds: readonly string[],
  columnId: string,
  value: unknown,
): Promise<BoardWrite> {
  const refused: BulkRefusal[] = [];
  for (let at = 0; at < itemIds.length; at += BULK_WRITE_CHUNK) {
    const result = await source.bulkSetCell({
      boardId,
      itemIds: [...itemIds.slice(at, at + BULK_WRITE_CHUNK)],
      columnId,
      value,
    });
    for (const entry of result?.refused ?? []) refused.push(entry);
  }
  return { written: itemIds.length - refused.length, refused };
}

/**
 * The sentence a board is skipped with — **the server's own**, never a rewrite of it.
 *
 * The 2026-09-17 incident printed the raw `Not allowed to cell.update` *and* stopped the loop;
 * the sentence was never the defect, the abort was. So the message is kept (it is what tells
 * the person what to do about the board) and only tidied: first line, no stack, bounded.
 */
export function bulkSkipReason(cause: unknown): string {
  const message = cause instanceof Error ? cause.message : typeof cause === 'string' ? cause : '';
  const first = message.split('\n')[0]?.trim() ?? '';
  if (first === '') return 'the board refused the change';
  /* The one sentence people actually meet, in words: the permission model's action name is not one. */
  if (/^Not allowed to cell\.update/.test(first)) return "you can't edit this board";
  return first.length > 160 ? `${first.slice(0, 157)}…` : first;
}

/** "2 tasks you may not edit were left out (Kickoff, Launch)" — the per-item refusal (P-293). */
export function describeRefusals(refused: readonly BulkRefusal[]): string {
  const head = `${refused.length} ${refused.length === 1 ? 'task' : 'tasks'} you may not edit ${
    refused.length === 1 ? 'was' : 'were'
  } left out`;
  const named = refused.slice(0, 3).map((entry) => entry.itemName);
  const rest = refused.length - named.length;
  return `${head} (${named.join(', ')}${rest > 0 ? `, +${rest} more` : ''})`;
}

/**
 * The previous value of one column, per row — the raw one, never the rendered one.
 *
 * `TaskRowView` carries `statusLabel` and `dueAt`, which are enough to *draw* a row and not to
 * restore one: a timeline cell holds a start date the pill never showed, and a status label can
 * be renamed. So the undo is built out of `itemContext().values[columnId]`, the canonical value
 * the api itself answered with, and is written back untouched by any column type's parser.
 *
 * A row whose item cannot be read back (archived, moved, or a column the person may not view)
 * contributes nothing rather than a guess.
 */
async function captureCellValues(
  source: TasksSource,
  rows: readonly TaskRowView[],
  columnId: string,
): Promise<BulkCellWrite[]> {
  const out: BulkCellWrite[] = [];
  for (const row of rows) {
    try {
      const context = await source.itemContext({
        boardId: row.boardId,
        itemId: row.itemId,
        itemName: row.itemName,
      });
      if (!context.found) continue;
      out.push({
        boardId: row.boardId,
        itemId: row.itemId,
        columnId,
        value: context.values[columnId] ?? null,
      });
    } catch {
      /* One unreadable row must not cost the whole action its undo. */
    }
  }
  return out;
}

/**
 * Puts a captured selection back — the toast's **Undo**.
 *
 * Rows that shared a previous value are written together, so undoing "3 tasks marked done" that
 * were all `Working on it` is one batch and not three writes. It goes through the same
 * `cells.bulkSet` as the action it reverses, so the permission check, the activity row and the
 * Tasks maintenance all apply to the undo exactly as they applied to the write.
 */
export async function applyBulkUndo(
  source: TasksSource,
  writes: readonly BulkCellWrite[],
): Promise<number> {
  const groups = new Map<string, { write: BulkCellWrite; itemIds: string[] }>();
  for (const write of writes) {
    const key = `${write.boardId} ${write.columnId} ${JSON.stringify(write.value ?? null)}`;
    const group = groups.get(key);
    if (group) group.itemIds.push(write.itemId);
    else groups.set(key, { write, itemIds: [write.itemId] });
  }
  let written = 0;
  for (const { write, itemIds } of groups.values()) {
    const result = await writeCells(source, write.boardId, itemIds, write.columnId, write.value);
    written += result.written;
  }
  return written;
}

/**
 * Marks every selected task done.
 *
 * The value written is the board's **first done label**, and it is built as
 * `{ index: <label id> }` because `task_board_mappings.done_labels` is defined by
 * `specs/M10-tasks.md` §2.3 as a list of status label **ids** — the mapping's own vocabulary,
 * carried by `tasks.mapping.get`. This is therefore the mapping contract being applied, not a
 * `if (type === 'status')` branch: nothing here reads the column's type at all, and a board
 * whose mapping has no done label is skipped rather than guessed at.
 */
export async function markTasksDone(
  source: TasksSource,
  rows: readonly TaskRowView[],
): Promise<BulkOutcome> {
  const outcome: BulkOutcome = { written: 0, skipped: [], undo: [] };
  const undoable = rows.length <= BULK_UNDO_LIMIT;
  for (const [boardId, boardRows] of byBoard(rows)) {
    const boardName = boardRows[0]?.boardName ?? boardId;
    /* One board's failure is one board's failure — P-293. */
    try {
      const mapping = await source.mapping({ boardId });
      if (mapping.statusColumnId === null) {
        outcome.skipped.push({ boardName, reason: 'no status column is mapped' });
        continue;
      }
      const doneLabel = mapping.doneLabels[0];
      if (doneLabel === undefined) {
        outcome.skipped.push({ boardName, reason: 'its status column marks no label as done' });
        continue;
      }
      /* Before the write, never after: afterwards every one of them reads back as done. */
      const captured = undoable
        ? await captureCellValues(source, boardRows, mapping.statusColumnId)
        : [];
      const result = await writeCells(
        source,
        boardId,
        boardRows.map((row) => row.itemId),
        mapping.statusColumnId,
        { index: Number(doneLabel) },
      );
      outcome.written += result.written;
      outcome.undo.push(...keepWritten(captured, result.refused));
      if (result.refused.length > 0) {
        outcome.skipped.push({ boardName, reason: describeRefusals(result.refused) });
      }
    } catch (cause) {
      outcome.skipped.push({ boardName, reason: bulkSkipReason(cause) });
    }
  }
  return outcome;
}

/* ── P-301 · bulk status ──────────────────────────────────────────────────── */

/**
 * One label the bulk bar may offer — a **text**, because that is the only thing two boards can
 * share (P-301 §2).
 *
 * Status label **ids** are per column: `1` is *Done* on one board and *Low* on another, which is
 * exactly the confusion that produced the 2026-09-17 data incident (P-294). So a selection that
 * spans boards is offered the labels whose *text* exists on every one of them, and the id is
 * re-resolved per board at the moment of writing. On a single board there is nothing to
 * intersect and the board's whole vocabulary is offered, in its own display order.
 */
export interface BulkStatusChoice {
  text: string;
  /** The label's hex on the **first** board that has it — a chip has to be one colour. */
  color: string | null;
  /** True when the label means *done* on **every** selected board (the ✓ in the menu). */
  isDone: boolean;
  /** How many of the selected boards carry a label with this text. */
  boards: number;
}

/** One board's status vocabulary, as the mapping and `tasks.mapping.options` answer it together. */
interface BoardStatusVocabulary {
  boardId: string;
  boardName: string;
  columnId: string;
  labels: TaskStatusLabelOptionView[];
  /** The label ids the board's mapping calls done (`specs/M10-tasks.md` §2.3). */
  doneLabels: string[];
}

/**
 * The mapped status column of one board, with its labels.
 *
 * Both reads are the mapping's own contract — `tasks.mapping.get` for *which* column plays the
 * status role and `tasks.mapping.options` for that column's labels — so nothing here asks a
 * column what **type** it is. That is what keeps CLAUDE.md rule 6: the one place that knows a
 * status column has labels is `services/tasks/mapping-options.ts`, which answers them already
 * shaped (id, text, colour, whether monday calls it done).
 *
 * A board whose mapping names no status column, or whose mapped column is not among the ones
 * the options answer (it was deleted, or the caller may not view it), has no vocabulary and is
 * skipped by name rather than guessed at.
 */
async function statusVocabularyOf(
  source: TasksSource,
  boardId: string,
  boardName: string,
): Promise<BoardStatusVocabulary | { skip: string }> {
  const mapping = await source.mapping({ boardId });
  if (mapping.statusColumnId === null) return { skip: 'no status column is mapped' };
  const options = await source.mappingOptions({ boardId });
  const column = options.statusColumns.find((entry) => entry.id === mapping.statusColumnId);
  if (column === undefined) return { skip: 'its status column is not readable' };
  return {
    boardId,
    boardName,
    columnId: mapping.statusColumnId,
    labels: column.labels ?? [],
    doneLabels: mapping.doneLabels,
  };
}

/**
 * What **Set status ▾** may offer for a selection — the labels, and the boards that were left
 * out of the offer entirely.
 *
 * The intersection is by text and it is **strict**: a label offered by this menu is one that
 * every board in the selection can take, so choosing it cannot half-apply. A label that only
 * some boards have is *not* offered here; the way to reach it is to narrow the selection, which
 * is what the count in `boards` is for when a caller wants to say so.
 */
export async function bulkStatusChoices(
  source: TasksSource,
  rows: readonly TaskRowView[],
): Promise<{ choices: BulkStatusChoice[]; skipped: { boardName: string; reason: string }[] }> {
  const skipped: { boardName: string; reason: string }[] = [];
  const vocabularies: BoardStatusVocabulary[] = [];
  for (const [boardId, boardRows] of byBoard(rows)) {
    const boardName = boardRows[0]?.boardName ?? boardId;
    try {
      const vocabulary = await statusVocabularyOf(source, boardId, boardName);
      if ('skip' in vocabulary) skipped.push({ boardName, reason: vocabulary.skip });
      else vocabularies.push(vocabulary);
    } catch (cause) {
      skipped.push({ boardName, reason: bulkSkipReason(cause) });
    }
  }
  if (vocabularies.length === 0) return { choices: [], skipped };

  /* The first board decides the order, because a menu has to have one and its own is monday's. */
  const [first, ...rest] = vocabularies;
  const choices: BulkStatusChoice[] = [];
  for (const label of first!.labels) {
    if (label.text === '' || choices.some((choice) => choice.text === label.text)) continue;
    const elsewhere = rest.map((board) => ({
      board,
      match: board.labels.find((entry) => entry.text === label.text),
    }));
    if (elsewhere.some((entry) => entry.match === undefined)) continue;
    const doneOn = (board: BoardStatusVocabulary, id: string) => board.doneLabels.includes(id);
    choices.push({
      text: label.text,
      color: label.color,
      isDone:
        doneOn(first!, label.id) &&
        elsewhere.every((entry) => doneOn(entry.board, entry.match!.id)),
      boards: vocabularies.length,
    });
  }
  return { choices, skipped };
}

/**
 * Sets every selected task's status to the label with this **text**.
 *
 * Per board: the mapping's status column, then that board's own label id for the text, written
 * as `{ index: <id> }` — the same shape `markTasksDone` writes, and the same one
 * `task_board_mappings.done_labels` is defined in. Nothing here reads a column's type, and a
 * board whose vocabulary has no such label is skipped **and named**, which is the card's
 * *"2 boards have no 'Stuck' label"*.
 *
 * Everything else — the per-board `try`, the per-item undo capture below
 * {@link BULK_UNDO_LIMIT}, the chunked `cells.bulkSet`, the refusals — is `markTasksDone`'s own
 * path, deliberately: *Mark done* is this action with the label chosen for you.
 */
export async function setTasksStatus(
  source: TasksSource,
  rows: readonly TaskRowView[],
  labelText: string,
): Promise<BulkOutcome> {
  const outcome: BulkOutcome = { written: 0, skipped: [], undo: [] };
  const undoable = rows.length <= BULK_UNDO_LIMIT;
  for (const [boardId, boardRows] of byBoard(rows)) {
    const boardName = boardRows[0]?.boardName ?? boardId;
    try {
      const vocabulary = await statusVocabularyOf(source, boardId, boardName);
      if ('skip' in vocabulary) {
        outcome.skipped.push({ boardName, reason: vocabulary.skip });
        continue;
      }
      const label = vocabulary.labels.find((entry) => entry.text === labelText);
      if (label === undefined) {
        outcome.skipped.push({ boardName, reason: `has no "${labelText}" label` });
        continue;
      }
      /* Before the write, never after: afterwards every one of them reads back as the new label. */
      const captured = undoable
        ? await captureCellValues(source, boardRows, vocabulary.columnId)
        : [];
      const result = await writeCells(
        source,
        boardId,
        boardRows.map((row) => row.itemId),
        vocabulary.columnId,
        { index: Number(label.id) },
      );
      outcome.written += result.written;
      outcome.undo.push(...keepWritten(captured, result.refused));
      if (result.refused.length > 0) {
        outcome.skipped.push({ boardName, reason: describeRefusals(result.refused) });
      }
    } catch (cause) {
      outcome.skipped.push({ boardName, reason: bulkSkipReason(cause) });
    }
  }
  return outcome;
}

/* ── P-301 · manual archive ───────────────────────────────────────────────── */

/**
 * Archives — or unarchives — every selected task, through `tasks.archive` / `tasks.unarchive`.
 *
 * It is **not** grouped by board and it writes no cell: the flag lives on the assignment row,
 * the permission is the row's own (mine, or assigned by me), and the server decides it per row.
 * So the call is one per chunk of {@link BULK_WRITE_CHUNK} items over the whole selection, and
 * what comes back is the items that carried no row this person may archive — reported as one
 * skipped line, because they are not a *board*'s refusal and naming a board for them would be
 * inventing a reason.
 *
 * The undo is the opposite call over exactly what moved, which is why `changed` is not enough on
 * its own: an item already archived writes nothing, and an undo that unarchived it would be a
 * new edit wearing an undo's label — the same rule `keepWritten` applies to cells.
 */
export async function archiveTasksBulk(
  source: TasksSource,
  rows: readonly TaskRowView[],
  direction: 'archive' | 'unarchive',
): Promise<BulkOutcome> {
  const outcome: BulkOutcome = { written: 0, skipped: [], undo: [] };
  /* One row per (item, assignee): two rows of the same item are one call, not two. */
  const itemIds = [...new Set(rows.map((row) => row.itemId))];
  /* Only the rows that would really move; an already-archived row is nothing to put back. */
  const moving = itemIds.filter((itemId) => {
    const row = rows.find((entry) => entry.itemId === itemId);
    return direction === 'archive' ? row?.archivedAt === null : row?.archivedAt !== null;
  });
  const skippedItems: string[] = [];

  for (let at = 0; at < itemIds.length; at += BULK_WRITE_CHUNK) {
    const chunk = itemIds.slice(at, at + BULK_WRITE_CHUNK);
    try {
      const result =
        direction === 'archive'
          ? await source.archiveTasks({ itemIds: chunk })
          : await source.unarchiveTasks({ itemIds: chunk });
      outcome.written += result.changed;
      for (const itemId of result.skipped ?? []) skippedItems.push(itemId);
    } catch (cause) {
      /* A whole chunk failing is not a board's fault, so it carries no board name (see below). */
      outcome.skipped.push({ boardName: '', reason: bulkSkipReason(cause) });
    }
  }

  if (skippedItems.length > 0) {
    const left = new Set(skippedItems);
    const names = rows.filter((row) => left.has(row.itemId)).map((row) => row.itemName);
    outcome.skipped.push({
      boardName: '',
      reason: `${left.size} ${left.size === 1 ? 'task' : 'tasks'} left out — ${
        left.size === 1 ? 'it is' : 'they are'
      } neither yours nor yours to delegate (${[
        ...new Set(names),
      ]
        .slice(0, 3)
        .join(', ')})`,
    });
  }

  const undoIds = moving.filter((itemId) => !skippedItems.includes(itemId));
  if (outcome.written > 0 && undoIds.length > 0) {
    outcome.undoArchive = {
      action: direction === 'archive' ? 'unarchive' : 'archive',
      itemIds: undoIds,
    };
  }
  return outcome;
}

/** The inverse call the toast's **Undo** performs for an archive (P-301 §3). */
export async function applyArchiveUndo(
  source: TasksSource,
  undo: NonNullable<BulkOutcome['undoArchive']>,
): Promise<number> {
  let written = 0;
  for (let at = 0; at < undo.itemIds.length; at += BULK_WRITE_CHUNK) {
    const chunk = undo.itemIds.slice(at, at + BULK_WRITE_CHUNK);
    const result =
      undo.action === 'archive'
        ? await source.archiveTasks({ itemIds: chunk })
        : await source.unarchiveTasks({ itemIds: chunk });
    written += result.changed;
  }
  return written;
}

/**
 * Sets every selected task's due date to one day.
 *
 * The date is handed to the **mapped column's own type** (`fromClipboard`, the registry member
 * that turns a piece of text into that type's canonical value), so a `date` column and a
 * `timeline` column each parse `2026-09-20` their own way and this function knows neither. A
 * type that refuses the text skips its board with the reason it gave.
 */
export async function setTasksDue(
  source: TasksSource,
  rows: readonly TaskRowView[],
  /** `YYYY-MM-DD`, as the bulk bar's date field produces it. */
  date: string,
): Promise<BulkOutcome> {
  const outcome: BulkOutcome = { written: 0, skipped: [], undo: [] };
  const undoable = rows.length <= BULK_UNDO_LIMIT;
  for (const [boardId, boardRows] of byBoard(rows)) {
    const first = boardRows[0];
    if (!first) continue;
    const boardName = first.boardName;
    /* One board's failure is one board's failure — P-293. */
    try {
      const mapping = await source.mapping({ boardId });
      if (mapping.dueColumnId === null) {
        outcome.skipped.push({ boardName, reason: 'no due date column is mapped' });
        continue;
      }
      const context = await source.itemContext({
        boardId,
        itemId: first.itemId,
        itemName: first.itemName,
      });
      const column = columnForRole(context, mapping, 'due');
      if (column === null) {
        outcome.skipped.push({ boardName, reason: 'the mapped due column is not visible to you' });
        continue;
      }
      const columns = context.columns.map((entry, index) => toBoardColumn(entry, index));
      const settings = normaliseColumnSettings(column.type, column.settingsStr ?? {});
      let value: unknown;
      try {
        value = getColumnUi(column.type).fromClipboard(date, settings, toRenderContext(context, columns));
      } catch (cause) {
        outcome.skipped.push({
          boardName,
          reason: cause instanceof Error ? cause.message : 'the due column refused that date',
        });
        continue;
      }
      const captured = undoable ? await captureCellValues(source, boardRows, column.id) : [];
      const result = await writeCells(
        source,
        boardId,
        boardRows.map((row) => row.itemId),
        column.id,
        value,
      );
      outcome.written += result.written;
      outcome.undo.push(...keepWritten(captured, result.refused));
      if (result.refused.length > 0) {
        outcome.skipped.push({ boardName, reason: describeRefusals(result.refused) });
      }
    } catch (cause) {
      outcome.skipped.push({ boardName, reason: bulkSkipReason(cause) });
    }
  }
  return outcome;
}

/**
 * The undo captures for the rows that were really written.
 *
 * A row the api refused was not written, so putting it "back" would be a *new* edit wearing an
 * undo's label — the same reasoning that makes the capture per item in the first place. A board
 * that threw keeps nothing at all, because its `captured` never reaches this function.
 */
function keepWritten(
  captured: readonly BulkCellWrite[],
  refused: readonly BulkRefusal[],
): BulkCellWrite[] {
  if (refused.length === 0) return [...captured];
  const left = new Set(refused.map((entry) => entry.itemId));
  return captured.filter((write) => !left.has(write.itemId));
}

/**
 * One toast line: what was written, how many boards were skipped, and why each one was.
 *
 * "3 tasks updated · 1 board skipped (Website: no due date column is mapped)" — P-293 asks for
 * the count *and* the names, because the incident's report was "nothing updates, I still see
 * 580": a person needs to read, in one line, that the rest of the selection did go through.
 */
export function describeBulkOutcome(outcome: BulkOutcome, noun = 'task', verb = 'updated'): string {
  const head = `${outcome.written} ${outcome.written === 1 ? noun : `${noun}s`} ${verb}`;
  if (outcome.skipped.length === 0) return head;
  /*
   * P-301 · a skip with **no board name** is not a board's refusal. The archive actions write no
   * cell and are not grouped by board, so "1 board skipped (Tasks: …)" would have named a board
   * that has nothing to do with it; those reasons stand as their own clause instead.
   */
  const boards = outcome.skipped.filter((entry) => entry.boardName !== '');
  const others = outcome.skipped.filter((entry) => entry.boardName === '');
  const parts = [head];
  if (boards.length > 0) {
    const count = `${boards.length} ${boards.length === 1 ? 'board' : 'boards'} skipped`;
    const detail = boards.map((entry) => `${entry.boardName}: ${entry.reason}`).join(' · ');
    parts.push(`${count} (${detail})`);
  }
  for (const entry of others) parts.push(entry.reason);
  return parts.join(' · ');
}
